import { useState } from 'react';
import { Logo, Wordmark } from '../Logo';

type State = 'idle' | 'sending' | 'done' | 'error';

export function Splash() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<State>('idle');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || state === 'sending') return;
    setState('sending');
    try {
      const res = await fetch('/api/interest', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source: 'splash' }),
      });
      setState(res.ok ? 'done' : 'error');
    } catch {
      setState('error');
    }
  }

  return (
    <div className="page">
      <div className="shell">
        <nav className="nav">
          <Wordmark />
          <a href="#request">Request access</a>
        </nav>
      </div>

      <header className="shell hero">
        <h1>
          Everyone came
          <br />
          with a camera.
        </h1>
        <p className="lede">
          One code on the table. Your guests and customers add their photos from any phone &mdash; no
          app, no account. You get every one of them, organised, cleared for you to use, and they
          never expire.
        </p>

        {state === 'done' ? (
          <p className="note" role="status" style={{ marginTop: 20, fontSize: 16 }}>
            Thank you &mdash; we&rsquo;ll be in touch.
          </p>
        ) : (
          <form className="signup" id="request" onSubmit={submit}>
            <label htmlFor="email" className="sr-only" style={srOnly}>
              Your email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@yourvenue.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button type="submit" disabled={state === 'sending'}>
              {state === 'sending' ? 'Sending…' : 'Request access'}
            </button>
          </form>
        )}

        <p className="note">
          {state === 'error'
            ? 'That did not send. Try again in a moment.'
            : 'Opening with a small group of venues and studios.'}
        </p>
      </header>

      <section className="shell film">
        <video
          src="/film.mp4"
          poster="/film-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-label="Twelve scattered points gathering into the Everyone's Camera mark"
        />
      </section>

      <section className="pillars">
        <div className="shell row">
          <div className="pillar">
            <div className="eyebrow">NO APP, NO ACCOUNT</div>
            <h2>Scan, add, done.</h2>
            <p>
              A guest points their camera at the code and picks their photos. Under a minute, nothing
              to download, nothing to sign into. Their grandmother can do it.
            </p>
          </div>
          <div className="pillar">
            <div className="eyebrow">CLEARED FOR USE</div>
            <h2>Yours to post.</h2>
            <p>
              Every upload carries a recorded permission to use it. So the photos are not just yours
              to look at &mdash; they are yours to publish, with the paperwork already done.
            </p>
          </div>
          <div className="pillar">
            <div className="eyebrow">NO EXPIRY</div>
            <h2>They stay.</h2>
            <p>
              Everything else in this category deletes your photos after a few months and calls it a
              plan. Ours do not expire. That is a decision, not a feature.
            </p>
          </div>
        </div>
      </section>

      <section className="shell audience">
        <div className="eyebrow">WHO IT IS FOR</div>
        <p>
          Wedding venues and hotels. Salons, studios and groomers. Breweries, farms and tours.
          Anywhere people already reach for their phone.
        </p>
      </section>

      <footer className="foot">
        <div className="shell row">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
            <Logo size={16} />
            Everyone&rsquo;s Camera
          </span>
          <span>everyonescamera.com</span>
        </div>
      </footer>
    </div>
  );
}

const srOnly: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
};
